import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calendar, Clock, Users, Dumbbell, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { startPayfastCheckout } from "@/lib/payments";
import { Button, Avatar, Tag, Loading, EmptyState } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { CheckoutSheet } from "@/components/CheckoutSheet";
import { formatDateLong, formatTime, formatR, LEVEL_LABEL } from "@/lib/format";

export default function ClassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [checkout, setCheckout] = useState(false);
  const c = useQuery({ queryKey: ["class", id], queryFn: () => api(`/classes/${id}`) });
  const badges = useQuery({ queryKey: ["me", "badges"], queryFn: () => api("/me/badges"), enabled: user?.role === "member" });
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api("/plans"), enabled: user?.role === "member" });

  const book = useMutation({
    mutationFn: async ({ method, usePoints }) => {
      const b = await api("/bookings/class", { method: "POST", body: { class_id: id, payment_method: method, use_points: usePoints } });
      if (b.status === "pending_payment") { await startPayfastCheckout(b.id, `/booking/${b.id}?pf=1`); return null; }
      return b;
    },
    onSuccess: (b) => {
      setCheckout(false);
      if (!b) return;
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["classes"] });
      navigate(`/booking/${b.id}?confirmed=1`);
    },
    onError: (e) => { setCheckout(false); toast.error(e.message); },
  });

  if (c.isLoading) return <Loading />;
  if (!c.data) return <><PageHeader /><EmptyState title="Class not found" /></>;
  const d = c.data;
  const sub = plans.data?.subscription;

  return (
    <div data-testid="class-detail-screen">
      <PageHeader onBack={() => navigate(-1)} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="relative h-64 lg:h-96 rounded-xl2 overflow-hidden">
          <img src={d.image} alt={d.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
          <span className="absolute top-4 left-4"><Tag label={d.category} /></span>
        </div>
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">{d.title}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Avatar uri={d.trainer_avatar} size={40} name={d.trainer_name} />
            <button onClick={() => navigate(`/trainers/${d.trainer_id}`)} className="font-body font-semibold text-ink">with {d.trainer_name}</button>
          </div>
          <div className="mt-5 space-y-3">
            {[[Calendar, formatDateLong(d.start_time)], [Clock, `${formatTime(d.start_time)} · ${d.duration_min} minutes`], [BarChart3, LEVEL_LABEL[d.level] || d.level], [Dumbbell, `Equipment: ${d.equipment || "None"}`], [Users, `${d.spots_left} of ${d.max_participants} spots left`]].map(([Icon, v], i) => (
              <div key={i} className="flex items-center gap-3"><Icon size={18} className="text-brand" /><span className="font-body text-[15px] text-ink">{v}</span></div>
            ))}
          </div>
          {d.description ? <p className="font-body text-mutedgrey mt-5 leading-relaxed">{d.description}</p> : null}
          <div className="flex items-center gap-4 mt-6">
            <div><div className="font-body text-xs text-mutedgrey">Price</div><div className="font-display text-3xl text-brand">{formatR(d.price)}</div></div>
            <Button testID="class-book-button" title={d.spots_left <= 0 ? "Class Full" : "Book This Class"} disabled={d.spots_left <= 0 || user?.role === "trainer"} onClick={() => setCheckout(true)} className="flex-1" />
          </div>
        </div>
      </div>

      <CheckoutSheet open={checkout} onClose={() => setCheckout(false)} title={d.title} subtitle={`${formatDateLong(d.start_time)} · ${formatTime(d.start_time)}`}
        amount={d.price} loading={book.isPending} onPay={(m, p) => book.mutate({ method: m, usePoints: p })}
        points={badges.data?.points ?? 0} fitpassCredits={sub ? sub.credits_left : undefined} />
    </div>
  );
}
